// NocodeBackend client via backend proxy.
// Security: the frontend never receives the NocodeBackend token.
// Les cookies de session admin (HttpOnly) sont envoyés avec chaque requête.

import axios from 'axios';

const API_BASE = '/api';

const api = axios.create({
  withCredentials: true,
});

function assertConfig() {
  if (!API_BASE) throw new Error('Configuration backend invalide.');
}

function normalizeAuthModePayload(raw) {
  let o = raw;
  if (typeof o === 'string') {
    try {
      o = JSON.parse(o);
    } catch {
      return {};
    }
  }
  if (!o || typeof o !== 'object') return {};
  return o;
}

/** Corps JSON d’une réponse API (auth). */
function unwrapAuthBody(raw) {
  return normalizeAuthModePayload(raw);
}

function authPayloadHasUser(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.success === false || data.error) return false;
  return Boolean(
    data.user ||
      data.session?.user ||
      data.data?.user ||
      (data.session && typeof data.session === 'object' && Object.keys(data.session).length > 0) ||
      (typeof data.session === 'string' && data.session.length > 0) ||
      data.access_token ||
      data.token
  );
}

function extractNcbErrorMessage(data) {
  if (!data || typeof data !== 'object') return '';
  const m = data.message ?? data.error ?? data.detail;
  if (typeof m === 'string' && m.trim()) return m.trim();
  if (Array.isArray(data.errors) && data.errors.length) return String(data.errors[0]);
  return '';
}

export async function getAuthMode() {
  assertConfig();
  try {
    const { data: raw, status } = await api.get(`${API_BASE}/auth/mode`, {
      validateStatus: () => true,
    });
    const data = normalizeAuthModePayload(raw);
    if (status === 503) {
      return { mode: "unconfigured", message: data?.message, envVisibility: data?.envVisibility };
    }
    if (status !== 200) {
      return {
        mode: "error",
        message: `Le serveur a répondu ${status}. Vérifiez que le dernier code est bien déployé sur Netlify.`,
      };
    }
    if (data?.mode === "ncb") {
      return { mode: data.mode };
    }
    return {
      mode: "error",
      message: "Réponse inattendue du serveur pour le mode d'authentification.",
    };
  } catch (e) {
    console.error(e);
    const status = e.response?.status;
    const data = normalizeAuthModePayload(e.response?.data);
    if (status === 503) {
      return { mode: "unconfigured", message: data?.message, envVisibility: data?.envVisibility };
    }
    if (status) {
      return {
        mode: "error",
        message: `Le serveur a répondu ${status}. Vérifiez que le dernier code est bien déployé sur Netlify.`,
      };
    }
    return {
      mode: "error",
      message: "Impossible de joindre l'API (réseau ou site non déployé).",
    };
  }
}

/** @returns {{ ok: true } | { ok: false, detail: string }} */
export async function ncbSignInEmail(email, password) {
  assertConfig();
  try {
    const res = await api.post(
      `${API_BASE}/auth/sign-in/email`,
      { email: String(email).trim(), password: String(password) },
      { validateStatus: () => true }
    );
    const data = unwrapAuthBody(res.data);

    if (res.status === 401 || res.status === 403) {
      return {
        ok: false,
        detail: extractNcbErrorMessage(data) || `Connexion refusée (${res.status}).`,
      };
    }
    if (res.status < 200 || res.status >= 300) {
      return {
        ok: false,
        detail: extractNcbErrorMessage(data) || `Erreur serveur (${res.status}).`,
      };
    }

    if (data.success === false || data.error) {
      return { ok: false, detail: extractNcbErrorMessage(data) || 'Connexion refusée.' };
    }
    if (authPayloadHasUser(data) || data.success === true) {
      return { ok: true };
    }

    const sess = await api.get(`${API_BASE}/auth/get-session`, { validateStatus: () => true });
    const sdata = unwrapAuthBody(sess.data);
    if (sess.status === 200 && authPayloadHasUser(sdata)) {
      return { ok: true };
    }
    return {
      ok: false,
      detail:
        extractNcbErrorMessage(sdata) ||
        'Session non établie sur ce domaine (cookies). Redéployez le site avec la dernière version du proxy.',
    };
  } catch (e) {
    console.error(e);
    const raw = e.response?.data;
    const msg = extractNcbErrorMessage(unwrapAuthBody(raw));
    return { ok: false, detail: msg || 'Erreur réseau ou serveur.' };
  }
}

/** Invalide la session NoCodeBackend (cookies). */
export async function adminLogout() {
  assertConfig();
  try {
    await api.post(`${API_BASE}/admin/logout`, {});
  } catch {
    /* ignore */
  }
}

export async function nbCreate(table, data) {
  assertConfig();
  const { data: res } = await api.post(`${API_BASE}/nb/create/${table}`, data);
  if (!res?.success) throw new Error(`Create failed: ${JSON.stringify(res)}`);
  return res.id;
}

export async function nbRead(table, filters = {}, limit = 1000) {
  assertConfig();
  const queryParams = { limit };
  for (const [k, v] of Object.entries(filters)) {
    if (v !== null && v !== undefined) queryParams[k] = v;
  }
  const { data: res } = await api.get(`${API_BASE}/nb/read/${table}`, { params: queryParams });
  if (res && res.success === false) {
    throw new Error(typeof res.error === 'string' ? res.error : 'Lecture impossible');
  }
  return res.rows || [];
}

export async function nbFindOne(table, filters) {
  const rows = await nbRead(table, filters, 1);
  return rows[0] || null;
}

export async function nbUpdate(table, id, data) {
  assertConfig();
  const { data: res } = await api.put(`${API_BASE}/nb/update/${table}/${id}`, data);
  if (!res?.success) throw new Error(`Update failed: ${JSON.stringify(res)}`);
}

export async function nbDelete(table, id) {
  assertConfig();
  const { data: res } = await api.delete(`${API_BASE}/nb/delete/${table}/${id}`);
  return Boolean(res?.success);
}
